/**
 * useVersionCheck - Auto-reload când versiunea aplicației se schimbă
 * 
 * Verifică periodic versiunea aplicației și forțează reload
 * când se detectează o versiune nouă (după deploy).
 */

import { useEffect, useRef, useCallback } from 'react';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minute
const VERSION_STORAGE_KEY = 'zenris_app_version';

export function useVersionCheck() {
  const isCheckingRef = useRef(false);
  
  const checkVersion = useCallback(async () => {
    // Previne verificări simultane
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;
    
    try {
      // Adăugăm timestamp pentru a evita cache
      const response = await fetch(`/api/v1/public/app-info?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.warn('[VersionCheck] Failed to fetch app info:', response.status);
        return;
      }
      
      const data = await response.json();
      const serverVersion = data.app_version;
      
      if (!serverVersion) {
        console.warn('[VersionCheck] No version in response');
        return;
      }
      
      const cachedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
      
      if (cachedVersion && cachedVersion !== serverVersion) {
        console.info(`[VersionCheck] New version detected: ${cachedVersion} → ${serverVersion}`);
        
        // Salvează noua versiune
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
        
        // Afișează notificare și reload
        if (window.confirm(
          `O versiune nouă a aplicației este disponibilă (${serverVersion}).\n\n` +
          `Doriți să reîncărcați pagina pentru a primi actualizările?`
        )) {
          // Force hard reload (bypass cache)
          window.location.reload();
        }
      } else if (!cachedVersion) {
        // Prima vizită - salvează versiunea
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
        console.info(`[VersionCheck] Version initialized: ${serverVersion}`);
      }
    } catch (error) {
      console.warn('[VersionCheck] Error checking version:', error);
    } finally {
      isCheckingRef.current = false;
    }
  }, []);
  
  useEffect(() => {
    // Verificare la încărcare (cu delay pentru a nu bloca UI)
    const initialTimeout = setTimeout(checkVersion, 3000);
    
    // Verificare periodică
    const interval = setInterval(checkVersion, VERSION_CHECK_INTERVAL);
    
    // Verificare la revenire în tab (visibility change)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkVersion]);
}

export default useVersionCheck;
